/**
 * Letter numbering (spec 12.2): the parish's pattern of named segments, the formatter
 * that turns a sequence number into "041/KSA/SB/IX/2026", the scope of a sequence (per
 * letter type or one for every type), the proposal for the next number, the parser that
 * reads the sequence back out of an edited number, and the duplicate detector that the
 * device runs after a sync (duplicates are flagged, never renumbered).
 *
 * The pattern's shape and its default live in validators/kolom (`polaNomorSuratSchema`,
 * `POLA_NOMOR_SURAT_BAWAAN`); this module only reads them. Pure and dialect-neutral.
 */
import { parsePartialDate } from './dates/partial'
import type { JenisSurat } from './registers'
import { ROMAWI } from './registers'
import { JENIS_SURAT } from './schema/contract'
import {
  POLA_NOMOR_SURAT_BAWAAN,
  type PolaNomorSurat,
  polaNomorSuratSchema,
} from './validators/kolom'

export type { PolaNomorSurat }
export { POLA_NOMOR_SURAT_BAWAAN }

export type SegmenNomor = PolaNomorSurat['segmen'][number]

/** What a number is built from: the sequence, the parish code, the type and the date. */
export interface KonteksNomor {
  urutan: number
  /** `unit_teritorial.kode` of the parish ("KSA"); an empty code leaves the segment out. */
  kodeParoki: string | null
  jenis: JenisSurat
  /** Partial date `YYYY-MM-DD` or `YYYY-MM`; a year alone prints the month as "-". */
  tanggal: string
}

/** The stored JSON (text or parsed) as a pattern; anything unreadable is the spec default. */
export function bacaPolaNomor(value: unknown): PolaNomorSurat {
  let parsed: unknown = value
  if (typeof parsed === 'string') {
    if (parsed.trim() === '') return POLA_NOMOR_SURAT_BAWAAN
    try {
      parsed = JSON.parse(parsed) as unknown
    } catch {
      return POLA_NOMOR_SURAT_BAWAAN
    }
  }
  const hasil = polaNomorSuratSchema.safeParse(parsed)
  return hasil.success ? hasil.data : POLA_NOMOR_SURAT_BAWAAN
}

/** The type code printed in the number: the parish's own, else the default ("SB"). */
export function kodeJenisSurat(pola: PolaNomorSurat, jenis: JenisSurat): string {
  return pola.kodeJenis[jenis] ?? POLA_NOMOR_SURAT_BAWAAN.kodeJenis[jenis] ?? jenis.toUpperCase()
}

/** "IX" for September; "-" outside 1 to 12. */
export function bulanRomawi(bulan: number): string {
  return Number.isInteger(bulan) && bulan >= 1 && bulan <= 12 ? (ROMAWI[bulan - 1] ?? '-') : '-'
}

/** The calendar year of a partial date; NaN when unreadable. */
export function tahunDariTanggal(tanggal: string): number {
  return parsePartialDate(tanggal)?.tahun ?? Number.NaN
}

function segmenTeks(
  pola: PolaNomorSurat,
  segmen: SegmenNomor,
  konteks: KonteksNomor,
): string | null {
  switch (segmen.jenis) {
    case 'urutan': {
      const lebar = segmen.lebar ?? 0
      return String(konteks.urutan).padStart(lebar, '0')
    }
    case 'kode_paroki':
      return konteks.kodeParoki === null || konteks.kodeParoki.trim() === ''
        ? null
        : konteks.kodeParoki.trim()
    case 'kode_jenis':
      return kodeJenisSurat(pola, konteks.jenis)
    case 'bulan_romawi': {
      const parts = parsePartialDate(konteks.tanggal)
      return bulanRomawi(parts?.bulan ?? 0)
    }
    case 'tahun': {
      const tahun = tahunDariTanggal(konteks.tanggal)
      return Number.isNaN(tahun) ? '-' : String(tahun)
    }
    case 'teks':
      return segmen.teks === undefined || segmen.teks === null || segmen.teks === ''
        ? null
        : segmen.teks
  }
}

/** "041/KSA/SB/IX/2026" from the pattern and its context. Empty segments are skipped. */
export function formatNomorSurat(pola: PolaNomorSurat, konteks: KonteksNomor): string {
  const parts: string[] = []
  for (const segmen of pola.segmen) {
    const teks = segmenTeks(pola, segmen, konteks)
    if (teks !== null) parts.push(teks)
  }
  return parts.join(pola.pemisah)
}

/**
 * The letter types that share one sequence with `jenis`: every type when the parish keeps
 * one agenda, else the type alone. The worker turns this into its `IN (...)` clause.
 */
export function jenisSeurutan(pola: PolaNomorSurat, jenis: JenisSurat): readonly JenisSurat[] {
  return pola.urutanBersama ? JENIS_SURAT : [jenis]
}

/** `max(urutan) + 1` for the scope and year; 1 when the year has no letter yet. */
export function usulanUrutan(urutanTerbesar: number | null | undefined): number {
  return urutanTerbesar === null || urutanTerbesar === undefined || urutanTerbesar < 0
    ? 1
    : Math.floor(urutanTerbesar) + 1
}

/**
 * The sequence number written in an edited number: the part at the `urutan` segment's
 * position when the number still follows the pattern, otherwise the first run of digits.
 * Null when there is no digit at all (the letter then takes no part in the proposal).
 */
export function bacaUrutan(nomor: string, pola: PolaNomorSurat): number | null {
  const index = pola.segmen.findIndex((s) => s.jenis === 'urutan')
  const parts = nomor.split(pola.pemisah)
  if (index >= 0 && parts.length === pola.segmen.length) {
    const bagian = parts[index]?.trim() ?? ''
    if (/^\d+$/.test(bagian)) return Number.parseInt(bagian, 10)
  }
  const digits = nomor.match(/\d+/)
  return digits === null ? null : Number.parseInt(digits[0], 10)
}

export interface NomorSuratRingkas {
  id: string
  nomorSurat: string
  parokiId: string | null
}

/**
 * Numbers used more than once inside one parish, as `nomor -> ids` (spec 12.2: flagged
 * on both devices after sync, nothing renumbered). Deleted letters must be left out by
 * the caller; comparison trims and ignores case so "041/ksa" and "041/KSA " collide.
 */
export function nomorGanda(
  rows: readonly NomorSuratRingkas[],
): Map<string, { parokiId: string | null; ids: string[] }> {
  const groups = new Map<string, { parokiId: string | null; ids: string[] }>()
  for (const row of rows) {
    const nomor = row.nomorSurat.trim().toUpperCase()
    if (nomor === '') continue
    const key = `${row.parokiId ?? ''}|${nomor}`
    const group = groups.get(key)
    if (group === undefined) groups.set(key, { parokiId: row.parokiId, ids: [row.id] })
    else group.ids.push(row.id)
  }
  const out = new Map<string, { parokiId: string | null; ids: string[] }>()
  for (const [key, group] of groups) {
    if (group.ids.length > 1) out.set(key.slice(key.indexOf('|') + 1), group)
  }
  return out
}
